/**
 * Bekreftelsesdialog for «Flytt herfra»-flyten (steg 6).
 * Åpnes når et mål er valgt i mål-velger-modusen (moveState.targetBinCode satt).
 * Gjenbruker heatmap-move-pipelinen: plan (dry-run med validering) → execute
 * (oppretter transordre). Samme overlay-stil som ContentsDialog.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, ArrowRight, CheckCircle2, Loader2, MoveRight, X } from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { heatmapMoveService } from '../../../../../services/heatmapMoveService';
import {
  HeatmapMoveReason,
  HeatmapMovePriority,
  HEATMAP_MOVE_REASON_LABELS,
  HEATMAP_MOVE_PRIORITY_LABELS,
  PlanMoveResponse,
} from '../../location-heatmap/heatmapMoveTypes';

/** Norsk tallformat for antall (samme som ContentsDialog) */
const quantityFormat = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 2 });

const REASON_OPTIONS: HeatmapMoveReason[] = [
  HeatmapMoveReason.Reorganization,
  HeatmapMoveReason.PickOptimization,
  HeatmapMoveReason.Consolidation,
  HeatmapMoveReason.Replenishment,
  HeatmapMoveReason.AbcReclassification,
  HeatmapMoveReason.Misplaced,
  HeatmapMoveReason.Rebuild,
  HeatmapMoveReason.Other,
];

const PRIORITY_OPTIONS: HeatmapMovePriority[] = [
  HeatmapMovePriority.Low,
  HeatmapMovePriority.Normal,
  HeatmapMovePriority.High,
  HeatmapMovePriority.Urgent,
];

const fieldClass =
  'w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500';

const MoveConfirmDialog: React.FC = () => {
  const { state, dispatch, warehouseId, notifyMoveExecuted } = useLayoutEditor();
  const { t } = useTranslation();
  const moveState = state.moveState;
  const isOpen = !!moveState?.targetBinCode;

  const [plan, setPlan] = useState<PlanMoveResponse | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState<HeatmapMoveReason>(HeatmapMoveReason.Reorganization);
  const [priority, setPriority] = useState<HeatmapMovePriority>(HeatmapMovePriority.Normal);
  const [comment, setComment] = useState('');
  const [executing, setExecuting] = useState(false);
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [createdOrderNo, setCreatedOrderNo] = useState<number | null>(null);

  const fromBinCode = moveState?.fromBinCode ?? null;
  const toBinCode = moveState?.targetBinCode ?? null;
  const itemNo = moveState?.itemNo ?? null;
  const maxQuantity = moveState?.maxQuantity ?? 0;

  // Vare-kandidaten fra plan-responsen (availableQuantity er fasit for maks)
  const planItem = useMemo(
    () => (plan && itemNo ? plan.items.find(item => item.itemNo === itemNo) ?? null : null),
    [plan, itemNo]
  );
  const availableQuantity = planItem?.availableQuantity ?? 0;

  // Planlegg (dry-run) ved åpning — nullstill alt skjemainnhold først
  useEffect(() => {
    if (!isOpen || !fromBinCode || !toBinCode || !itemNo) return;
    let cancelled = false;
    setPlan(null);
    setPlanError(null);
    setPlanLoading(true);
    setQuantity('');
    setReason(HeatmapMoveReason.Reorganization);
    setPriority(HeatmapMovePriority.Normal);
    setComment('');
    setExecuting(false);
    setExecuteError(null);
    setCreatedOrderNo(null);

    heatmapMoveService.plan({ warehouseID: warehouseId, fromBinCode, toBinCode, itemNo })
      .then(result => {
        if (cancelled) return;
        setPlan(result);
        // Default-antall: min(radens antall, tilgjengelig), clamp 1..available
        const candidate = result.items.find(item => item.itemNo === itemNo);
        const available = candidate?.availableQuantity ?? 0;
        const initial = Math.max(1, Math.min(maxQuantity, available));
        setQuantity(available > 0 ? String(initial) : '');
      })
      .catch((err: unknown) => {
        if (!cancelled) setPlanError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setPlanLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, fromBinCode, toBinCode, itemNo, maxQuantity, warehouseId]);

  // Tilbake til mål-velgeren (behold flytte-modusen) — brukes av Avbryt/X/Esc
  const backToPicker = () => dispatch({ type: 'SET_MOVE_TARGET', binCode: null });
  // Avslutt hele flytte-modusen — brukes av Lukk etter suksess
  const closeAll = () => dispatch({ type: 'CANCEL_MOVE' });

  // Esc: tilbake til mål-velgeren (capture + stopPropagation slik at canvasens
  // Esc-handler ikke avbryter hele modusen samtidig)
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        dispatch({ type: 'SET_MOVE_TARGET', binCode: null });
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, dispatch]);

  if (!isOpen || !moveState || !fromBinCode || !toBinCode || !itemNo) return null;

  const parsedQuantity = Number.parseFloat(quantity.replace(',', '.'));
  const quantityValid =
    Number.isFinite(parsedQuantity) && parsedQuantity >= 1 && parsedQuantity <= availableQuantity;

  const hasErrors = !!plan && plan.errors.length > 0;
  const canExecute =
    !!plan && plan.canProceed && !hasErrors && !planLoading && !executing &&
    availableQuantity > 0 && quantityValid && !createdOrderNo;

  const clampQuantity = () => {
    if (!Number.isFinite(parsedQuantity)) return;
    const clamped = Math.max(1, Math.min(parsedQuantity, Math.max(1, availableQuantity)));
    if (clamped !== parsedQuantity) setQuantity(String(clamped));
  };

  const handleExecute = async () => {
    if (!canExecute) return;
    setExecuting(true);
    setExecuteError(null);
    try {
      const result = await heatmapMoveService.execute({
        warehouseID: warehouseId,
        fromBinCode,
        toBinCode,
        itemNo,
        quantity: parsedQuantity,
        reason,
        priority,
        comment: comment.trim() || undefined,
      });
      if (result.success) {
        setCreatedOrderNo(result.stcOrderNo ?? 0);
        // Flyttet er nå en åpen transordre: oppdater pil-laget + invalider innsikt-cachen
        notifyMoveExecuted();
      } else {
        setExecuteError(
          result.errorMessage ||
          t('manager.modules.warehouseLayout.move.executeFailed', {}, 'Flyttingen feilet')
        );
      }
    } catch (err: unknown) {
      setExecuteError(err instanceof Error ? err.message : String(err));
    } finally {
      setExecuting(false);
    }
  };

  // «Flytt mer»: tilbake til mål-velgeren med samme vare (moveState beholdes)
  const handleMoveMore = () => dispatch({ type: 'SET_MOVE_TARGET', binCode: null });

  const destination = plan?.destinationInfo;

  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center bg-black/40"
      onMouseDown={e => {
        // Klikk utenfor dialogboksen: tilbake til mål-velgeren
        if (e.target === e.currentTarget && !executing) backToPicker();
      }}
    >
      <div className="w-[460px] max-w-[92%] max-h-[85%] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <MoveRight className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {t('manager.modules.warehouseLayout.move.confirmTitle', {}, 'Flytt vare')}
          </h3>
          <button
            onClick={backToPicker}
            disabled={executing}
            className="ml-auto p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0 disabled:opacity-40"
            title={t('common.buttons.close', {}, 'Lukk')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Vare + fra → til */}
          <div className="space-y-1.5">
            <p className="text-sm">
              <span className="font-mono font-semibold text-gray-900">{itemNo}</span>
              {moveState.itemDescription && (
                <span className="text-gray-500"> — {moveState.itemDescription}</span>
              )}
            </p>
            <p className="flex items-center gap-2 text-sm font-mono text-gray-700">
              <span>{fromBinCode}</span>
              <ArrowRight className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span className="font-semibold">{toBinCode}</span>
            </p>
          </div>

          {planLoading && (
            <div className="flex items-center gap-2 py-4 text-gray-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
              {t('manager.modules.warehouseLayout.move.planLoading', {}, 'Kontrollerer flyttingen...')}
            </div>
          )}

          {!planLoading && planError && (
            <div className="flex items-start gap-2 p-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p>{t('manager.modules.warehouseLayout.move.planFailed', {}, 'Kunne ikke planlegge flyttingen')}</p>
                <p className="text-xs text-red-500 mt-0.5">{planError}</p>
              </div>
            </div>
          )}

          {!planLoading && plan && (
            <>
              {/* Destinasjonsinfo */}
              {destination && (
                <div className="p-2.5 bg-gray-50 rounded-md text-xs text-gray-600 space-y-0.5">
                  {destination.binTypeName && (
                    <div>
                      {t('manager.modules.warehouseLayout.move.destType', {}, 'Type')}
                      {': '}<span className="text-gray-800">{destination.binTypeName}</span>
                    </div>
                  )}
                  <div>
                    {t('manager.modules.warehouseLayout.move.destCurrentQty', {}, 'Nåværende saldo')}
                    {': '}<span className="text-gray-800">{quantityFormat.format(destination.currentQuantity)}</span>
                  </div>
                  {destination.maxQuantity !== undefined && destination.maxQuantity !== null && (
                    <div>
                      {t('manager.modules.warehouseLayout.move.destMaxQty', {}, 'Maks')}
                      {': '}<span className="text-gray-800">{quantityFormat.format(destination.maxQuantity)}</span>
                    </div>
                  )}
                  {destination.availableSpace !== undefined && destination.availableSpace !== null && (
                    <div>
                      {t('manager.modules.warehouseLayout.move.destAvailableSpace', {}, 'Ledig plass')}
                      {': '}<span className="text-gray-800">{quantityFormat.format(destination.availableSpace)}</span>
                    </div>
                  )}
                  {destination.sharedBinYesNo && (
                    <div className="text-amber-700">
                      {t('manager.modules.warehouseLayout.move.destShared', {}, 'Delt lokasjon')}
                    </div>
                  )}
                </div>
              )}

              {/* Valideringsfeil (blokkerer) og advarsler (tillater) */}
              {plan.errors.map((message, index) => (
                <p key={`err-${index}`} className="flex items-start gap-1.5 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {message.message}
                </p>
              ))}
              {plan.warnings.map((message, index) => (
                <p key={`warn-${index}`} className="flex items-start gap-1.5 text-sm text-amber-600">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {message.message}
                </p>
              ))}

              {availableQuantity <= 0 && !hasErrors && (
                <p className="flex items-start gap-1.5 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {t('manager.modules.warehouseLayout.move.noAvailableQty', {}, 'Ingen tilgjengelig mengde å flytte')}
                </p>
              )}

              {/* Skjema — skjules etter vellykket flytting */}
              {!createdOrderNo && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                      {t('manager.modules.warehouseLayout.move.quantityLabel', {}, 'Antall')}
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={availableQuantity}
                      value={quantity}
                      onChange={e => setQuantity(e.target.value)}
                      onBlur={clampQuantity}
                      disabled={availableQuantity <= 0 || executing}
                      className={fieldClass}
                    />
                    <p className="text-xs text-gray-400 mt-0.5">
                      {t(
                        'manager.modules.warehouseLayout.move.availableHint',
                        { '0': quantityFormat.format(availableQuantity) },
                        `Tilgjengelig: ${quantityFormat.format(availableQuantity)}`
                      )}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                        {t('manager.modules.warehouseLayout.move.reasonLabel', {}, 'Årsak')}
                      </label>
                      <select
                        value={reason}
                        onChange={e => setReason(Number(e.target.value) as HeatmapMoveReason)}
                        disabled={executing}
                        className={fieldClass}
                      >
                        {REASON_OPTIONS.map(option => (
                          <option key={option} value={option}>
                            {HEATMAP_MOVE_REASON_LABELS[option]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                        {t('manager.modules.warehouseLayout.move.priorityLabel', {}, 'Prioritet')}
                      </label>
                      <select
                        value={priority}
                        onChange={e => setPriority(Number(e.target.value) as HeatmapMovePriority)}
                        disabled={executing}
                        className={fieldClass}
                      >
                        {PRIORITY_OPTIONS.map(option => (
                          <option key={option} value={option}>
                            {HEATMAP_MOVE_PRIORITY_LABELS[option]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                      {t('manager.modules.warehouseLayout.move.commentLabel', {}, 'Kommentar (valgfri)')}
                    </label>
                    <input
                      type="text"
                      value={comment}
                      onChange={e => setComment(e.target.value)}
                      disabled={executing}
                      className={fieldClass}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Utfør-feil */}
          {executeError && (
            <p className="flex items-start gap-1.5 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              {executeError}
            </p>
          )}

          {/* Suksess */}
          {createdOrderNo !== null && (
            <div className="flex items-start gap-2 p-2.5 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" />
              {t(
                'manager.modules.warehouseLayout.move.successMessage',
                { '0': createdOrderNo },
                `Transordre ${createdOrderNo} opprettet`
              )}
            </div>
          )}
        </div>

        {/* Footer-knapper */}
        <div className="flex gap-2 p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          {createdOrderNo === null ? (
            <>
              <button
                onClick={backToPicker}
                disabled={executing}
                className="flex-1 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-40"
              >
                {t('manager.modules.warehouseLayout.move.cancelButton', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleExecute}
                disabled={!canExecute}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {executing && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('manager.modules.warehouseLayout.move.executeButton', {}, 'Utfør flytt')}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleMoveMore}
                className="flex-1 px-3 py-2 text-sm text-emerald-700 bg-white border border-emerald-300 rounded-md hover:bg-emerald-50"
              >
                {t('manager.modules.warehouseLayout.move.moveMoreButton', {}, 'Flytt mer')}
              </button>
              <button
                onClick={closeAll}
                className="flex-1 px-3 py-2 text-sm text-white bg-emerald-600 rounded-md hover:bg-emerald-700"
              >
                {t('common.buttons.close', {}, 'Lukk')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MoveConfirmDialog;
